import { useQuery } from '@tanstack/react-query';
import { type ReactElement, useMemo, useState } from 'react';
import { CoverageUploadAction } from '@/features/codeCoverage/components/CoverageUploadAction';
import { queryCodeDirectories } from '@/features/codeCoverage/data/codeDirectories';
import { queryCoverageFiles } from '@/features/codeCoverage/data/coverageFiles';
import { queryCoverageRuns } from '@/features/codeCoverage/data/coverageRuns';
import {
  CoverageDetailTablesPanel,
  type CoverageDetailTablesPanelTab,
} from '@/features/codeCoverage/pages/CoverageDetailTablesPanel';
import { CoverageDirectoriesTable } from '@/features/codeCoverage/pages/CoverageDirectoriesTable';
import {
  coverageDirectoriesTableDefinition,
  type CoverageDirectoriesSortKey,
} from '@/features/codeCoverage/pages/coverageDirectoriesTableDefinition';
import { mapCodeDirectoryDtosToCoverageRows } from '@/features/codeCoverage/pages/coverageEntityRows';
import { CoveragePageFiltersToolbar } from '@/features/codeCoverage/pages/CoveragePageFiltersToolbar';
import {
  COVERAGE_CHART_PAGE_SIZE,
  toDateEndIso,
  toDateStartIso,
} from '@/features/codeCoverage/pages/coverageRunsPage.constants';
import {
  buildCodeEntityTagOptions,
  buildCoverageRunOptions,
} from '@/features/codeCoverage/pages/coverageRunsPageFilterOptions';
import { CoverageRunsTable } from '@/features/codeCoverage/pages/CoverageRunsTable';
import {
  coverageRunsTableDefinition,
  type CoverageRunsSortKey,
} from '@/features/codeCoverage/pages/coverageRunsTableDefinition';
import { useCoverageRefreshVersion } from '@/features/codeCoverage/state/coverageRefreshSignal';
import { PageLoadingFallback } from '@/features/metrics/components/PageLoadingFallback';
import { useOrganisationFilter } from '@/features/organisations/providers/organisationHooks';
import { useProjectFilter } from '@/features/projects/hooks/projectHooks';
import { type DateQuickFilter, useDateWindow } from '@/features/shared/hooks/useDateWindow';
import { queryKeys } from '@/shared/hooks';
import { DataStateMessage, Stack, Text } from '@/ui';
import {
  buildDateRangeFilters,
  buildInFilter,
  buildProjectScopedFilters,
  mergeFilters,
} from '@/ui/composites/grid/gridFilters';
import { ListPageTemplate } from '@/ui/composites/scaffolds';
import type { GridColumnVisibilityModel } from '@/ui/primitives';
import type {
  CodeDirectoryDto,
  CoverageFileDto,
  CoverageRunDto,
  CoverageRunFailureKind,
  CoverageRunFailureSubkind,
  QueryResult,
} from '@flowqa/shared';
import type { Dayjs } from 'dayjs';

type CoverageDirectoriesListTab = 'directories' | 'runs';

const preservePreviousQueryData = <T,>(previousData: T | undefined): T | undefined => previousData;

export const CoverageDirectoriesPage = (): ReactElement => {
  const { selectedOrganisationId } = useOrganisationFilter();
  const { selectedProjectId } = useProjectFilter();
  const coverageRefreshVersion = useCoverageRefreshVersion();
  const [activeTab, setActiveTab] = useState<CoverageDirectoriesListTab>('directories');
  const [dateQuickFilter, setDateQuickFilter] = useState<DateQuickFilter>('7d');
  const [fromDate, setFromDate] = useState<Dayjs | null>(null);
  const [toDate, setToDate] = useState<Dayjs | null>(null);
  const [filterCoverageRuns, setFilterCoverageRuns] = useState<string[]>([]);
  const [filterFailureKinds, setFilterFailureKinds] = useState<CoverageRunFailureKind[]>([]);
  const [filterFailureSubkinds, setFilterFailureSubkinds] = useState<CoverageRunFailureSubkind[]>(
    [],
  );
  const [filterTags, setFilterTags] = useState<string[]>([]);
  const [columnVisibilityModel, setColumnVisibilityModel] = useState<GridColumnVisibilityModel>(
    coverageDirectoriesTableDefinition.defaultColumnVisibilityModel,
  );
  const [sortKey] = useState<CoverageDirectoriesSortKey>(
    coverageDirectoriesTableDefinition.defaultSortKey,
  );
  const [sortDirection] = useState<'asc' | 'desc'>(
    coverageDirectoriesTableDefinition.defaultSortDirection,
  );
  const [runsColumnVisibilityModel, setRunsColumnVisibilityModel] =
    useState<GridColumnVisibilityModel>(coverageRunsTableDefinition.defaultColumnVisibilityModel);
  const [runsSortKey] = useState<CoverageRunsSortKey>(coverageRunsTableDefinition.defaultSortKey);
  const [runsSortDirection] = useState<'asc' | 'desc'>(
    coverageRunsTableDefinition.defaultSortDirection,
  );

  const dateWindow = useDateWindow({
    dateQuickFilter,
    fromDate,
    toDate,
  });

  const baseFilters = useMemo(
    () =>
      mergeFilters(
        buildProjectScopedFilters(selectedProjectId),
        buildDateRangeFilters(
          'capturedAt',
          toDateStartIso(dateWindow.startDay),
          toDateEndIso(dateWindow.endDay),
        ),
      ),
    [dateWindow.endDay, dateWindow.startDay, selectedProjectId],
  );
  const coverageRunFilters = useMemo(
    () =>
      mergeFilters(
        baseFilters,
        buildInFilter('failureKind', filterFailureKinds),
        buildInFilter('failureSubkind', filterFailureSubkinds),
      ),
    [baseFilters, filterFailureKinds, filterFailureSubkinds],
  );
  const coverageRunFilterKey = useMemo(
    () =>
      [
        dateWindow.startDay ?? 'none',
        dateWindow.endDay ?? 'none',
        filterFailureKinds.slice().sort().join(',') || 'all-failure-kinds',
        filterFailureSubkinds.slice().sort().join(',') || 'all-failure-details',
        `refresh-${String(coverageRefreshVersion)}`,
      ].join(':'),
    [
      coverageRefreshVersion,
      dateWindow.endDay,
      dateWindow.startDay,
      filterFailureKinds,
      filterFailureSubkinds,
    ],
  );

  const runsQuery = useQuery<QueryResult<CoverageRunDto>>({
    queryKey: queryKeys.coverageRuns(
      selectedOrganisationId ?? null,
      selectedProjectId,
      `directories:${selectedProjectId ?? 'all'}:${coverageRunFilterKey}`,
    ),
    queryFn: async () =>
      await queryCoverageRuns(
        {
          page: 1,
          pageSize: COVERAGE_CHART_PAGE_SIZE,
          filters: coverageRunFilters,
        },
        selectedOrganisationId ?? null,
      ),
    placeholderData: preservePreviousQueryData,
  });

  const recentRuns = runsQuery.data?.items ?? [];
  const coverageRunOptions = useMemo(() => buildCoverageRunOptions(recentRuns), [recentRuns]);
  const selectedRuns = useMemo(
    () =>
      recentRuns.filter(
        (run) => filterCoverageRuns.length === 0 || filterCoverageRuns.includes(run.id),
      ),
    [filterCoverageRuns, recentRuns],
  );
  const selectedRunIds = useMemo(() => selectedRuns.map((run) => run.id), [selectedRuns]);

  const directoriesQuery = useQuery<QueryResult<CodeDirectoryDto>>({
    queryKey: queryKeys.codeDirectories(
      selectedOrganisationId ?? null,
      selectedProjectId,
      `directories:${selectedRunIds.join(',') || 'all-runs'}:${dateWindow.startDay ?? 'none'}:${dateWindow.endDay ?? 'none'}:${filterTags.join(',') || 'all-tags'}:${sortKey}:${sortDirection}:refresh-${String(coverageRefreshVersion)}`,
    ),
    enabled: selectedRunIds.length > 0,
    queryFn: async () =>
      await queryCodeDirectories(
        {
          page: 1,
          pageSize: 100,
          filters: mergeFilters(
            buildProjectScopedFilters(selectedProjectId),
            buildInFilter('latestCoverageRunId', selectedRunIds),
            buildInFilter('tags', filterTags),
          ),
          sorts: [
            {
              field: 'normalizedPath',
              direction: sortDirection,
            },
          ],
        },
        selectedOrganisationId ?? null,
      ),
    placeholderData: preservePreviousQueryData,
  });

  const tagOptionsQuery = useQuery<QueryResult<CodeDirectoryDto>>({
    queryKey: queryKeys.codeDirectories(
      selectedOrganisationId ?? null,
      selectedProjectId,
      `directories-tag-options:${selectedRunIds.join(',') || 'all-runs'}:${dateWindow.startDay ?? 'none'}:${dateWindow.endDay ?? 'none'}:refresh-${String(coverageRefreshVersion)}`,
    ),
    enabled: selectedRunIds.length > 0,
    queryFn: async () =>
      await queryCodeDirectories(
        {
          page: 1,
          pageSize: 100,
          filters: mergeFilters(
            buildProjectScopedFilters(selectedProjectId),
            buildInFilter('latestCoverageRunId', selectedRunIds),
          ),
        },
        selectedOrganisationId ?? null,
      ),
    placeholderData: preservePreviousQueryData,
  });

  const tagOptions = useMemo(
    () => buildCodeEntityTagOptions(tagOptionsQuery.data?.items ?? []),
    [tagOptionsQuery.data?.items],
  );
  const directoryRows = useMemo(() => {
    if (selectedRunIds.length === 0) {
      return [];
    }

    return mapCodeDirectoryDtosToCoverageRows(directoriesQuery.data?.items ?? []);
  }, [directoriesQuery.data?.items, selectedRunIds.length]);
  const visibleDirectoryPaths = useMemo(
    () => directoryRows.map((row) => row.normalizedPath),
    [directoryRows],
  );
  const coverageFileHistoryQuery = useQuery<QueryResult<CoverageFileDto>>({
    queryKey: queryKeys.coverageFiles(
      selectedOrganisationId ?? null,
      selectedProjectId,
      `directories-history:${selectedRunIds.join(',') || 'none'}:${visibleDirectoryPaths.join(',') || 'none'}:${dateWindow.startDay ?? 'none'}:${dateWindow.endDay ?? 'none'}:refresh-${String(coverageRefreshVersion)}`,
    ),
    enabled: activeTab === 'runs' && selectedRunIds.length > 0 && visibleDirectoryPaths.length > 0,
    queryFn: async () =>
      await queryCoverageFiles(
        {
          page: 1,
          pageSize: 100,
          filters: mergeFilters(
            buildProjectScopedFilters(selectedProjectId),
            buildDateRangeFilters(
              'capturedAt',
              toDateStartIso(dateWindow.startDay),
              toDateEndIso(dateWindow.endDay),
            ),
            buildInFilter('coverageRunId', selectedRunIds),
          ),
          sorts: [{ field: 'capturedAt', direction: 'desc' }],
        },
        selectedOrganisationId ?? null,
      ),
    placeholderData: preservePreviousQueryData,
  });
  const visibleCoverageHistory = useMemo(
    () =>
      (coverageFileHistoryQuery.data?.items ?? []).filter((row) =>
        visibleDirectoryPaths.some(
          (directoryPath) =>
            row.normalizedPath === directoryPath ||
            row.normalizedPath.startsWith(`${directoryPath}/`),
        ),
      ),
    [coverageFileHistoryQuery.data?.items, visibleDirectoryPaths],
  );
  const visibleHistoryRunIds = useMemo(
    () => new Set(visibleCoverageHistory.map((row) => row.coverageRunId)),
    [visibleCoverageHistory],
  );
  const filteredRuns = useMemo(
    () =>
      recentRuns.filter((run) => {
        return visibleHistoryRunIds.has(run.id);
      }),
    [recentRuns, visibleHistoryRunIds],
  );
  const filteredRunIdsKey = useMemo(
    () => filteredRuns.map((run) => run.id).join(',') || 'empty',
    [filteredRuns],
  );
  const directoryRowKey = useMemo(
    () => directoryRows.map((row) => row.id).join(',') || 'empty',
    [directoryRows],
  );
  const directoriesTableLoading =
    runsQuery.isPending ||
    (selectedRunIds.length > 0 && directoriesQuery.isPending) ||
    (directoryRows.length === 0 && (runsQuery.isFetching || directoriesQuery.isFetching));
  const runsTableLoading =
    (selectedRunIds.length > 0 &&
      visibleDirectoryPaths.length > 0 &&
      coverageFileHistoryQuery.isPending) ||
    (filteredRuns.length === 0 && coverageFileHistoryQuery.isFetching);
  const listTabs = useMemo<CoverageDetailTablesPanelTab[]>(
    () => [
      {
        value: 'directories',
        label: 'Directories',
        toolbar: <div />,
        content: (
          <CoverageDirectoriesTable
            directories={directoryRows}
            projectId={selectedProjectId}
            loading={directoriesTableLoading}
            queryKey={queryKeys.dataGrid(
              `coverage-directories-page:${sortKey}:${sortDirection}:${directoryRowKey}`,
            )}
            initialSortModel={[
              {
                field: coverageDirectoriesTableDefinition.sortFieldMap[sortKey],
                sort: sortDirection,
              },
            ]}
            columnVisibilityModel={columnVisibilityModel}
            onColumnVisibilityModelChange={setColumnVisibilityModel}
          />
        ),
      },
      {
        value: 'runs',
        label: 'Runs',
        toolbar: <div />,
        content: (
          <CoverageRunsTable
            key={filteredRunIdsKey}
            runs={filteredRuns}
            projectId={selectedProjectId}
            loading={runsTableLoading}
            queryKey={queryKeys.dataGrid(
              `coverage-directories-runs-page:${runsSortKey}:${runsSortDirection}:${filteredRunIdsKey}`,
            )}
            initialSortModel={[
              {
                field: coverageRunsTableDefinition.sortFieldMap[runsSortKey],
                sort: runsSortDirection,
              },
            ]}
            columnVisibilityModel={runsColumnVisibilityModel}
            onColumnVisibilityModelChange={setRunsColumnVisibilityModel}
          />
        ),
      },
    ],
    [
      columnVisibilityModel,
      directoryRowKey,
      directoryRows,
      directoriesTableLoading,
      filteredRunIdsKey,
      filteredRuns,
      runsColumnVisibilityModel,
      runsSortDirection,
      runsSortKey,
      selectedProjectId,
      runsTableLoading,
      sortDirection,
      sortKey,
    ],
  );
  const error =
    runsQuery.error ??
    directoriesQuery.error ??
    tagOptionsQuery.error ??
    (activeTab === 'runs' ? coverageFileHistoryQuery.error : null);
  const hasCoverageData =
    recentRuns.length > 0 || directoryRows.length > 0 || filteredRuns.length > 0;
  const showLoadErrorState = error !== null && !hasCoverageData;
  const showPageLoading = runsQuery.isLoading && !runsQuery.isError;

  if (showPageLoading) {
    return <PageLoadingFallback loadingMessageKey="coverageDirectories" />;
  }

  return (
    <ListPageTemplate
      headerActionsMode="separate"
      title={
        <Text role="pageTitle" component="h1">
          Directories
        </Text>
      }
      titleActions={<CoverageUploadAction />}
      headerActions={
        <CoveragePageFiltersToolbar
          coverageRunOptions={coverageRunOptions}
          dateQuickFilter={dateQuickFilter}
          filterCoverageRuns={filterCoverageRuns}
          filterFailureKinds={filterFailureKinds}
          filterFailureSubkinds={filterFailureSubkinds}
          filterTags={filterTags}
          fromDate={fromDate}
          onFilterTagsChange={setFilterTags}
          onDateQuickFilterChange={setDateQuickFilter}
          onFilterCoverageRunsChange={setFilterCoverageRuns}
          onFilterFailureKindsChange={setFilterFailureKinds}
          onFilterFailureSubkindsChange={setFilterFailureSubkinds}
          onFromDateChange={setFromDate}
          onToDateChange={setToDate}
          tagOptions={tagOptions}
          toDate={toDate}
          showDirectoryFilter={false}
          showFileFilter={false}
        />
      }
      summary={null}
      charts={null}
      content={
        <Stack gap="md">
          {showLoadErrorState ? (
            <DataStateMessage
              message="Failed to load coverage directories"
              tone="red"
              variant="card"
            />
          ) : null}
          <CoverageDetailTablesPanel
            activeTab={activeTab}
            onTabChange={(nextTab) => {
              setActiveTab(nextTab as CoverageDirectoriesListTab);
            }}
            tabsAriaLabel="Coverage directories page tabs"
            tabs={[
              {
                ...listTabs[0],
                toolbar: null,
              },
              {
                ...listTabs[1],
                toolbar: null,
              },
            ]}
          />
        </Stack>
      }
    />
  );
};
